import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom'; 
import { db } from '../firebase';
import { collection, getDocs, query, where, orderBy, doc, getDoc } from 'firebase/firestore';
// CORREÇÃO: Adicionado FaExclamationTriangle na importação
import { FaCalendarDay, FaClock, FaMusic, FaMapMarkerAlt, FaExclamationCircle, FaCheckCircle, FaExternalLinkAlt, FaUsers, FaEdit, FaBirthdayCake, FaExclamationTriangle, FaYoutube, FaGuitar, FaCloud, FaHeadphones } from 'react-icons/fa';

// Ordem de prioridade para exibição da equipe
const ROLE_ORDER = [
  'Ministro de Música',
  'Violão',
  'Teclado',
  'Baixo',
  'Bateria',
  'Guitarra',
  'Sax',
  'Vocal',
];

function sortTeam(team) {
  if (!team) return [];
  return [...team].sort((a, b) => {
    const indexA = ROLE_ORDER.indexOf(a.role);
    const indexB = ROLE_ORDER.indexOf(b.role);
    // Roles not in the list go just before Vocal (treat as instruments)
    const orderA = indexA === -1 ? ROLE_ORDER.length - 1.5 : indexA;
    const orderB = indexB === -1 ? ROLE_ORDER.length - 1.5 : indexB;
    return orderA - orderB;
  });
}

export default function Dashboard() {
  const { userDetails } = useAuth();
  const navigate = useNavigate();
  const [nextSchedule, setNextSchedule] = useState(null);
  const [upcomingSchedules, setUpcomingSchedules] = useState([]);
  const [noticeBoard, setNoticeBoard] = useState('');
  const [birthdays, setBirthdays] = useState([]);
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      if (!userDetails || !userDetails.musicianId) {
        setLoading(false);
        return;
      }

      try {
        const today = new Date();
        const currentMonth = today.getMonth(); 

        // 1. Carregar Escalas
        const todayStr = today.toISOString().split('T')[0];
        const schedulesRef = collection(db, "schedules");
        const q = query(schedulesRef, where("date", ">=", todayStr), orderBy("date", "asc"));
        const querySnapshot = await getDocs(q);

        const myList = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          const amIScheduled = data.team && data.team.some(member => member.id === userDetails.musicianId);
          if (amIScheduled) myList.push({ ...data, id: doc.id });
        });

        // Ordenar escalas por data e depois por hora
        myList.sort((a, b) => {
          if (a.date !== b.date) {
            return a.date.localeCompare(b.date);
          }
          const timeA = a.time || '00:00';
          const timeB = b.time || '00:00';
          return timeA.localeCompare(timeB);
        });

        if (myList.length > 0) {
          setNextSchedule(myList[0]);
          setUpcomingSchedules(myList.slice(1, 6)); 
        } else {
          setNextSchedule(null);
          setUpcomingSchedules([]);
        }

        // 2. Carregar Músicas para live lookup dos links
        const songsRef = collection(db, "songs");
        const songsSnap = await getDocs(songsRef);
        const songsList = songsSnap.docs.map(doc => ({ ...doc.data(), id: doc.id }));
        setSongs(songsList);

        // 3. Carregar Avisos
        const docSnap = await getDoc(doc(db, "church_settings", "config"));
        if (docSnap.exists()) {
            setNoticeBoard(docSnap.data().noticeBoard || '');
        }

        // 3. Carregar Aniversariantes
        const musiciansRef = collection(db, "musicians");
        const musSnap = await getDocs(query(musiciansRef, where("isActive", "==", true)));
        
        const birthdaysList = [];
        musSnap.forEach(doc => {
            const m = doc.data();
            if (m.birthDate) {
                const parts = m.birthDate.split('-');
                const month = parseInt(parts[1]) - 1;
                const day = parseInt(parts[2]);

                if (month === currentMonth) {
                    birthdaysList.push({ name: m.name, day: day });
                }
            }
        });

        birthdaysList.sort((a, b) => a.day - b.day);
        setBirthdays(birthdaysList);

      } catch (error) {
        console.error("Erro ao carregar dashboard:", error);
      }
      setLoading(false);
    };

    loadData();
  }, [userDetails]);

  if (!userDetails) return null;

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day); 
    return date.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  const isMyMusicMinistry = (schedule) => {
    if (!schedule || !schedule.team) return false;
    const myEntry = schedule.team.find(m => m.id === userDetails.musicianId);
    return myEntry && myEntry.role === "Ministro de Música";
  };

  const handleEditClick = (scheduleId) => {
    navigate('/escalas', { state: { editScheduleId: scheduleId } });
  };

  // === NOVO: Lógica para verificar se a escala está incompleta ===
  const isNextScheduleIncomplete = nextSchedule && (
      (!nextSchedule.repertoire || nextSchedule.repertoire.length === 0) ||
      (!nextSchedule.team || nextSchedule.team.length === 0) ||
      !(nextSchedule.team.some(m => m.role === 'Ministro de Música'))
  );

  if (loading) return <div className="p-10 text-center text-gray-500">Carregando seu painel...</div>;

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-800">
          Olá, <span className="text-brand-blue">{userDetails?.musicianName?.split(' ')[0] || 'Visitante'}</span>!
        </h1>
        <p className="text-gray-500 text-sm">Aqui está o resumo da sua agenda.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        <div className="lg:col-span-2 space-y-6">
           
           {/* === CARD DESTAQUE: PRÓXIMA ESCALA === */}
           {nextSchedule ? (
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border-t-4 border-brand-gold">
              <div className="bg-brand-blue p-4 text-white flex justify-between items-center">
                <h3 className="font-bold text-lg flex items-center gap-2">
                    <FaCalendarDay /> Sua Próxima Escala
                    {/* ÍCONE DE ALERTA ADICIONADO AQUI */}
                    {isNextScheduleIncomplete && (
                        <span className="text-orange-500 text-xl animate-pulse bg-white rounded-full p-0.5" title="Atenção: Equipe incompleta ou Repertório vazio">
                            <FaExclamationTriangle />
                        </span>
                    )}
                </h3>
                {isMyMusicMinistry(nextSchedule) ? (
                    <button onClick={() => handleEditClick(nextSchedule.id)} className="bg-white text-brand-blue text-xs font-bold px-3 py-1.5 rounded shadow hover:bg-gray-100 flex items-center gap-2 transition">
                        <FaEdit /> Editar Repertório
                    </button>
                ) : (
                    <span className="bg-brand-gold text-brand-blue text-xs font-bold px-2 py-1 rounded uppercase">Confirmado</span>
                )}
              </div>
              <div className="p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">{nextSchedule.title}</h2>
                
                {/* LINHA DE DATA */}
                <div className="flex items-center gap-2 text-gray-600 mb-6">
                    <FaClock className="text-brand-gold" />
                    <span className="capitalize">{formatDate(nextSchedule.date)} às {nextSchedule.time}h</span>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                    {/* Repertório */}
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                        <h4 className="text-sm font-bold text-gray-500 uppercase mb-3 flex items-center gap-2"><FaMusic /> Repertório</h4>
                        <ul className="space-y-2">
                            {nextSchedule.repertoire && nextSchedule.repertoire.length > 0 ? (
                            nextSchedule.repertoire.map((song, idx) => {
                                const liveSong = songs.find(s => s.id === song.id) || {};
                                const youtubeLink = liveSong.link || song.link || '';
                                const cifraLink = liveSong.linkCifra || song.linkCifra || '';
                                const driveLink = liveSong.linkDrive || song.linkDrive || '';
                                const versaoLink = liveSong.linkVersao || song.linkVersao || '';
                                const artist = liveSong.artist || song.artist || '';
                                return (
                                    <li key={idx} className="flex flex-col border-b border-gray-200 py-2 last:border-0">
                                        <div className="flex justify-between items-start text-sm w-full gap-2">
                                            <div className="min-w-0 flex-1">
                                                <span className="text-gray-700 font-bold">{idx + 1}. {song.title}</span>
                                                {artist && (
                                                    <span className="block sm:inline sm:ml-1 text-xs sm:text-sm text-gray-400">
                                                        <span className="hidden sm:inline">— </span>{artist}
                                                    </span>
                                                )}
                                            </div>
                                            <span className="text-brand-blue font-bold bg-blue-100 px-2 py-0.5 rounded text-xs flex-shrink-0 mt-0.5">{song.customKey || song.key || '?'}</span>
                                        </div>
                                        {/* Linha de Links abaixo da música */}
                                        {(youtubeLink || cifraLink || driveLink || versaoLink) && (
                                            <div className="flex items-center gap-4 mt-2">
                                                {youtubeLink && (<a href={youtubeLink} target="_blank" rel="noreferrer" className="text-red-500 hover:opacity-80 transition" title="YouTube"><FaYoutube size={16} /></a>)}
                                                {cifraLink && (<a href={cifraLink} target="_blank" rel="noreferrer" className="text-orange-500 hover:opacity-80 transition" title="Cifra"><FaGuitar size={16} /></a>)}
                                                {driveLink && (<a href={driveLink} target="_blank" rel="noreferrer" className="text-blue-500 hover:opacity-80 transition" title="Google Drive"><FaCloud size={16} /></a>)}
                                                {versaoLink && (<a href={versaoLink} target="_blank" rel="noreferrer" className="text-purple-500 hover:opacity-80 transition" title="Nossa Versão"><FaHeadphones size={16} /></a>)}
                                            </div>
                                        )}
                                    </li>
                                );
                            })) : (<p className="text-gray-400 text-sm italic">Músicas ainda não definidas.</p>)}
                        </ul>
                    </div>
                    
                    {/* Equipe */}
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                        <h4 className="text-sm font-bold text-brand-blue uppercase mb-3 flex items-center gap-2"><FaUsers /> Equipe</h4>
                        <div className="flex flex-wrap gap-2">
                            {nextSchedule.team && sortTeam(nextSchedule.team).map((member, idx) => {
                                const isMe = member.id === userDetails.musicianId;
                                const isMinister = member.role === "Ministro de Música";
                                let styleClass = "bg-white text-gray-700 border-gray-200";
                                if (isMe) styleClass = "bg-brand-blue text-white border-brand-blue shadow-sm";
                                else if (isMinister) styleClass = "bg-orange-100 text-orange-900 border-orange-200";

                                return (
                                    <span key={idx} className={`text-sm px-3 py-1 rounded-full border ${styleClass}`}>
                                        <strong>{member.name}</strong> <span className={`text-xs ${isMe ? 'text-gray-200' : 'opacity-70'}`}>({member.role})</span>
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white p-8 rounded-xl shadow text-center border-l-4 border-gray-300">
              <FaCheckCircle className="text-4xl text-green-500 mx-auto mb-3" />
              <h3 className="text-lg font-bold text-gray-700">Tudo tranquilo!</h3>
              <p className="text-gray-500">Você não tem escalas agendadas para os próximos dias.</p>
            </div>
          )}

          {/* LISTA: PRÓXIMAS DATAS */}
          {upcomingSchedules.length > 0 && (
            <div>
              <h3 className="text-lg font-bold text-gray-700 mb-3 ml-1">Próximas Datas</h3>
              <div className="bg-white rounded-lg shadow overflow-hidden divide-y divide-gray-100">
                {upcomingSchedules.map((sched) => (
                  <div key={sched.id} className="p-4 hover:bg-gray-50 transition">
                    <div className="flex justify-between items-start mb-2">
                        <div>
                            <p className="font-bold text-brand-blue text-base">{sched.title}</p>
                            <p className="text-sm text-gray-500 capitalize flex items-center gap-1">
                                <FaClock className="text-xs"/> {formatDate(sched.date)} • {sched.time}h
                            </p>
                        </div>
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded font-medium">Em breve</span>
                    </div>
                    
                    <div className="text-xs text-gray-500 flex items-start gap-2 mt-1 bg-gray-50 p-2 rounded">
                        <FaUsers className="mt-0.5 flex-shrink-0 text-gray-400" />
                        <p className="leading-relaxed">
                            {sched.team && sortTeam(sched.team).map((m, i) => (
                                <span key={i}>
                                    <span className={m.id === userDetails.musicianId ? "font-bold text-brand-blue" : ""}>
                                        {m.name.split(' ')[0]}
                                    </span> 
                                    <span className="opacity-70"> ({m.role})</span>
                                    {i < sched.team.length - 1 && ', '}
                                </span>
                            ))}
                        </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* COLUNA DIREITA (1/3) */}
        <div className="space-y-6">
          
          {/* QUADRO DE AVISOS */}
          <div className="bg-yellow-50 p-6 rounded-xl border border-yellow-200 shadow-sm">
            <h3 className="font-bold text-yellow-800 flex items-center gap-2 mb-3">
              <FaExclamationCircle /> Quadro de Avisos
            </h3>
            {noticeBoard ? (
                <p className="text-sm text-yellow-900 opacity-90 leading-relaxed whitespace-pre-wrap bg-white/50 p-3 rounded border border-yellow-100">
                {noticeBoard}
                </p>
            ) : (
                <p className="text-sm text-yellow-700 italic">Sem avisos no momento.</p>
            )}
          </div>

          {/* ANIVERSARIANTES DO MÊS */}
          <div className="bg-white p-6 rounded-xl shadow-sm border-t-4 border-pink-400">
            <h3 className="font-bold text-gray-700 flex items-center gap-2 mb-4">
              <FaBirthdayCake className="text-pink-400" /> Aniversariantes
            </h3>
            
            {birthdays.length > 0 ? (
                <ul className="space-y-2">
                    {birthdays.map((bday, index) => (
                        <li key={index} className="flex items-center justify-between text-sm border-b border-gray-100 pb-2 last:border-0 last:pb-0">
                            <span className="text-gray-800 font-medium">{bday.name}</span>
                            <span className="text-pink-500 font-bold bg-pink-50 px-2 py-1 rounded text-xs">
                                Dia {bday.day}
                            </span>
                        </li>
                    ))}
                </ul>
            ) : (
                <p className="text-sm text-gray-400 italic text-center py-2">Nenhum aniversariante este mês.</p>
            )}
          </div>

        </div>

      </div>
    </div>
  );
}